// web/src/plugin-hub.ts
// /plugin-manuals.html —— 通用插件手册首页（导航「插件手册」的落地页）。
// 只做两件事：讲清 Blender / Unreal 两个插件的关系，并把用户分流到各平台的手册。
// 平台各自的通用说明在 /manuals/blender/ 与 /manuals/unreal/。

import { getLang, initI18n } from "./i18n";
import { pickText, PRODUCT_META } from "./site-data";

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ] as string,
  );
}

/** Unreal 侧额外有：附赠模块（不演奏乐器，只做口型）与两个公共页签的说明页 */
const UNREAL_BONUS = {
  slug: "singer",
  icon: "🎤",
  nameZh: "Singer（附赠模块）",
  nameEn: "Singer (bonus module)",
};

const UNREAL_COMMON = {
  slug: "common",
  icon: "🧰",
  nameZh: "公共模块：骨骼映射 / 口型同步",
  nameEn: "Common modules: B/C Mapping & Lip Sync",
};

const BLENDER_ROOT = "/manuals/blender/";
const UNREAL_ROOT = "/manuals/unreal/";

function renderPlatforms(): void {
  const lang = getLang();
  const isZh = lang === "zh";
  const root = document.getElementById("hub-platforms");
  if (!root) return;

  const listLink = (href: string, label: string, icon: string) =>
    `<li><a href="${href}">${icon} ${escapeHtml(label)}</a></li>`;

  const blenderList = PRODUCT_META.map((p) =>
    listLink(
      `${BLENDER_ROOT}${p.slug}.html`,
      pickText(p.title, lang),
      p.icon,
    ),
  ).join("");

  const unrealList = [
    ...PRODUCT_META.map((p) =>
      listLink(
        `${UNREAL_ROOT}${p.slug}.html`,
        pickText(p.title, lang),
        p.icon,
      ),
    ),
    listLink(
      `${UNREAL_ROOT}${UNREAL_COMMON.slug}.html`,
      isZh ? UNREAL_COMMON.nameZh : UNREAL_COMMON.nameEn,
      UNREAL_COMMON.icon,
    ),
    listLink(
      `${UNREAL_ROOT}${UNREAL_BONUS.slug}.html`,
      isZh ? UNREAL_BONUS.nameZh : UNREAL_BONUS.nameEn,
      UNREAL_BONUS.icon,
    ),
  ].join("");

  const card = (
    icon: string,
    title: string,
    desc: string,
    href: string,
    listTitle: string,
    list: string,
    cta: string,
  ) => `<article class="feature-card">
  <h3>${icon} ${escapeHtml(title)}</h3>
  <p class="muted">${escapeHtml(desc)}</p>
  <div class="feature-actions">
    <a class="btn secondary" href="${href}">${escapeHtml(cta)}</a>
  </div>
  <p class="muted small">${escapeHtml(listTitle)}</p>
  <ul class="hub-link-list">${list}</ul>
</article>`;

  root.innerHTML = [
    card(
      "🧩",
      isZh ? "Blender 插件手册" : "Blender Plugin Manual",
      isZh
        ? "在 Blender 里管理演奏者、搭控制器、存取演奏状态、导入导出人物信息并生成动画，最后渲染出片。"
        : "Manage performers, build controllers, store performance states, import/export performer info and generate animation inside Blender, then render.",
      BLENDER_ROOT,
      isZh ? "含 7 个乐器的面板说明" : "Covers 7 instruments' panels",
      blenderList,
      isZh ? "打开 Blender 插件手册" : "Open the Blender plugin manual",
    ),
    card(
      "🎬",
      isZh ? "Unreal 插件手册" : "Unreal Plugin Manual",
      isZh
        ? "在引擎里把软件生成的数据写进 Level Sequence、驱动 Control Rig 与材质／形态动画，并处理口型、骨骼映射等引擎侧工作。"
        : "Write the apps' data into Level Sequence tracks, drive Control Rigs plus material and morph animation in the engine, and handle lip sync and bone mapping.",
      UNREAL_ROOT,
      isZh
        ? "含 7 个乐器 + 公共模块 + 1 个附赠模块的页签说明"
        : "Covers 7 instruments, the shared modules and 1 bonus module",
      unrealList,
      isZh ? "打开 Unreal 插件手册" : "Open the Unreal plugin manual",
    ),
  ].join("\n");
}

function renderMatrix(): void {
  const lang = getLang();
  const isZh = lang === "zh";
  const body = document.getElementById("hub-matrix-body");
  if (!body) return;

  const rows: string[] = [];
  for (const p of PRODUCT_META) {
    rows.push(`<tr>
  <td>${p.icon} ${escapeHtml(pickText(p.title, lang))}</td>
  <td><a href="${BLENDER_ROOT}${p.slug}.html">${isZh ? "Blender 插件说明" : "Blender plugin guide"}</a></td>
  <td><a href="${UNREAL_ROOT}${p.slug}.html">${isZh ? "Unreal 插件说明" : "Unreal plugin guide"}</a></td>
</tr>`);
  }
  rows.push(`<tr>
  <td>${UNREAL_COMMON.icon} ${escapeHtml(isZh ? UNREAL_COMMON.nameZh : UNREAL_COMMON.nameEn)}</td>
  <td class="muted">${isZh ? "—（Blender 端在各自的工具区里）" : "— (inside each tool menu on the Blender side)"}</td>
  <td><a href="${UNREAL_ROOT}${UNREAL_COMMON.slug}.html">${isZh ? "Unreal 公共模块说明" : "Unreal common-module guide"}</a></td>
</tr>`);
  rows.push(`<tr>
  <td>${UNREAL_BONUS.icon} ${escapeHtml(isZh ? UNREAL_BONUS.nameZh : UNREAL_BONUS.nameEn)}</td>
  <td class="muted">${isZh ? "—（无 Blender 端）" : "— (no Blender side)"}</td>
  <td><a href="${UNREAL_ROOT}${UNREAL_BONUS.slug}.html">${isZh ? "Unreal 插件说明" : "Unreal plugin guide"}</a></td>
</tr>`);
  body.innerHTML = rows.join("\n");
}

function renderStaticText(): void {
  const isZh = getLang() === "zh";
  const set = (id: string, text: string) => {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  };

  document.title = isZh ? "插件手册 | MusicDoll" : "Plugin Manuals | MusicDoll";
  set("hub-crumb-home", isZh ? "首页" : "Home");
  set("hub-crumb-current", isZh ? "插件手册" : "Plugin Manuals");
  set("hub-title", isZh ? "插件手册" : "Plugin Manuals");
  set(
    "hub-sub",
    isZh
      ? "MusicDoll 的插件是**免费的桥接插件**：软件（Rust 端）负责算演奏数据，插件负责把数据落到制作软件里。插件有两个平台——Blender 侧做角色与控制器，Unreal 侧驱动引擎的 Control Rig 与 Sequencer。先选你要看的平台，再进入对应乐器／模块的界面说明。"
      : "MusicDoll's plugins are the **free bridge**: the instrument apps compute the performance data, and the plugins land it inside your production software. There are two platforms — Blender builds characters and controllers, Unreal drives the engine's Control Rigs and Sequencer. Pick the platform you need, then jump into the matching instrument or module guide.",
  );
  const chips = document.getElementById("hub-chips");
  if (chips) {
    chips.innerHTML = [
      `<span class="chip free">${isZh ? "Blender 插件免费" : "Blender plugin is free"}</span>`,
      `<span class="chip free">${isZh ? "Unreal 插件免费" : "Unreal plugin is free"}</span>`,
      `<span class="chip">${isZh ? "两个平台，同一套数据" : "Two platforms, one data pipeline"}</span>`,
    ].join("");
  }
  set("hub-platform-title", isZh ? "选择平台" : "Pick a platform");
  set("hub-matrix-title", isZh ? "按乐器直接进入" : "Jump straight to an instrument");
  set(
    "hub-matrix-sub",
    isZh
      ? "两个平台的手册都是「一篇通用说明 + 每个乐器／模块一篇专属说明」，Unreal 侧多一篇两个公共页签（骨骼映射、口型同步）的说明。下面是同一件乐器在两边的入口。"
      : "Each platform's manual is one common guide plus one page per instrument/module; on the Unreal side there is one extra page for the two shared tabs (bone mapping, lip sync). Below are both entries for each instrument.",
  );
  set("hub-th-instrument", isZh ? "乐器 / 模块" : "Instrument / module");
  set("hub-th-blender", isZh ? "Blender 插件" : "Blender plugin");
  set("hub-th-unreal", isZh ? "Unreal 插件" : "Unreal plugin");
  set("hub-next-title", isZh ? "接下来" : "Next steps");
  set("hub-quickstart", isZh ? "看各产品的 Quick Start" : "Browse the Quick Starts");
  set("hub-products", isZh ? "返回产品矩阵" : "Back to products");
  set("hub-support", isZh ? "支持文档" : "Support docs");
  set("hub-buy", isZh ? "购买授权" : "Buy a license");
  set(
    "hub-footer",
    isZh
      ? "插件报错请附上对应的日志（Blender：系统控制台；Unreal：Output Log）。"
      : "When reporting a plugin error, attach the matching log (Blender: system console; Unreal: Output Log).",
  );

  // 说明文字里的 **粗体** 需要渲染成 <strong>（textContent 不会解释标记）
  const sub = document.getElementById("hub-sub");
  if (sub) {
    sub.innerHTML = escapeHtml(sub.textContent || "").replace(
      /\*\*([^*]+)\*\*/g,
      "<strong>$1</strong>",
    );
  }
}

function render(): void {
  renderStaticText();
  renderPlatforms();
  renderMatrix();
}

function main(): void {
  initI18n();
  render();
  document.addEventListener("langchange", render);
}

main();
